import { useRef, useState } from 'react';

import { BpkDropdownChip } from '@skyscanner/backpack-web/bpk-component-chip';
import BpkPopover from '@skyscanner/backpack-web/bpk-component-popover';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import PartnersFilterOptionList from '../PartnersFilterOptionList';

import type { FilterOptionOnClick } from '../../types';
import type { FilterOption } from '@shared/types/frontend/ubff/hotel-detail-price';
import type { I18nService } from '@shared/types/i18n';

import STYLES from './PartnersFilter.module.scss';

import { withI18n } from '@shared/ui/contexts/i18n';

const cls = cssModules(STYLES);

type Props = {
  i18n: I18nService;
  partnerChips: FilterOption[];
  partnersFilterTitle: string;
  filterOptionOnClick: FilterOptionOnClick;
  isMobile: boolean;
};

const PartnersFilter = ({
  filterOptionOnClick,
  i18n,
  isMobile,
  partnerChips,
  partnersFilterTitle,
}: Props) => {
  const target = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const onOpen = () => {
    setIsOpen(true);
  };

  const onClose = () => {
    setIsOpen(false);
  };

  const sortedPartnerChips = partnerChips
    .slice()
    .sort((firstOption: FilterOption, secondOption: FilterOption) => {
      const firstOptionName = firstOption.displayName.toLowerCase();
      const secondOptionName = secondOption.displayName.toLowerCase();
      return firstOptionName.localeCompare(secondOptionName);
    });

  const hasSelectedPartnerChip = sortedPartnerChips.some(
    (chip) => chip.selected,
  );
  const title = i18n.translate('Filters_label_provider');

  return isMobile ? (
    <>
      <BpkDropdownChip
        accessibilityLabel={partnersFilterTitle}
        selected={hasSelectedPartnerChip}
        onClick={() => onOpen()}
      >
        {partnersFilterTitle}
      </BpkDropdownChip>
      <PartnersFilterOptionList
        partnerChips={sortedPartnerChips}
        filterOptionOnClick={filterOptionOnClick}
        isOpen={isOpen}
        onClose={onClose}
        isMobile
        title={title}
      />
    </>
  ) : (
    <BpkPopover
      id="offer-filter-partners-popover"
      label={title}
      closeButtonIcon
      target={
        <span ref={target} data-testid="offer-filter-partners">
          <BpkDropdownChip
            accessibilityLabel={partnersFilterTitle}
            selected={hasSelectedPartnerChip}
            onClick={() => onOpen()}
          >
            {partnersFilterTitle}
          </BpkDropdownChip>
        </span>
      }
      isOpen={isOpen}
      onClose={onClose}
      padded={false}
      labelAsTitle
    >
      <div className={cls('PartnersFilter__filterSection')}>
        <PartnersFilterOptionList
          partnerChips={sortedPartnerChips}
          filterOptionOnClick={filterOptionOnClick}
          isMobile={false}
          isOpen={isOpen}
          onClose={onClose}
          title={title}
        />
      </div>
    </BpkPopover>
  );
};

export default withI18n(PartnersFilter);
